import { computeBill, type BillBreakdown } from "@bulbee/pricing";
import { useEffect, useMemo, useState } from "react";

import { loadPlansForZip } from "./data";
import {
  clearFilters,
  filtersActive,
  parseFinderQuery,
  serializeFinderQuery,
  type FinderQuery,
  type RateTypeFilter,
  type SortKey,
  type TermFilter,
} from "./finderQuery";
import { PlanCard } from "./PlanCard";
import type { PublishedPlan } from "./types";

export interface Priced {
  plan: PublishedPlan;
  bill: BillBreakdown;
}

type Status =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "not-served" }
  | { kind: "error"; message: string }
  | { kind: "ready"; plans: PublishedPlan[]; tdus: string[]; generatedAt: string };

const USAGE_PRESETS = [500, 1000, 1500, 2000];

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "price", label: "Price, low to high" },
  { value: "-price", label: "Price, high to low" },
  { value: "term", label: "Term, shortest" },
  { value: "-term", label: "Term, longest" },
  { value: "-renewable", label: "Renewable, highest" },
  { value: "renewable", label: "Renewable, lowest" },
  { value: "provider", label: "Provider, A to Z" },
];

function readQuery(): FinderQuery {
  return parseFinderQuery(window.location.search);
}

function matchesTerm(months: number | null, term: TermFilter): boolean {
  if (term === "any") return true;
  if (months == null) return false;
  switch (term) {
    case "m2m":
      return months <= 1;
    case "short":
      return months >= 2 && months <= 11;
    case "12":
      return months === 12;
    case "24":
      return months === 24;
    case "36":
      return months >= 36;
  }
}

function matchesType(rateType: string, filter: RateTypeFilter): boolean {
  return filter === "any" || rateType.toLowerCase() === filter;
}

function matchesName(plan: PublishedPlan, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return plan.rep.toLowerCase().includes(needle) || plan.product.toLowerCase().includes(needle);
}

function comparePriced(a: Priced, b: Priced, sort: SortKey): number {
  const descending = sort.startsWith("-");
  const key = descending ? sort.slice(1) : sort;
  let primary = 0;
  switch (key) {
    case "price":
      primary = a.bill.avg_cents_per_kwh - b.bill.avg_cents_per_kwh;
      break;
    case "term":
      primary = (a.plan.term_months ?? 0) - (b.plan.term_months ?? 0);
      break;
    case "renewable":
      primary = (a.plan.renewable_pct ?? -1) - (b.plan.renewable_pct ?? -1);
      break;
    case "provider":
      primary =
        a.plan.rep.localeCompare(b.plan.rep) || a.plan.product.localeCompare(b.plan.product);
      break;
  }
  if (primary !== 0) return descending ? -primary : primary;
  return a.plan.plan_id - b.plan.plan_id;
}

export function PlanFinder() {
  const initial = readQuery();
  const [zipInput, setZipInput] = useState(initial.zip ?? "");
  const [query, setQuery] = useState<FinderQuery>(initial);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  useEffect(() => {
    const next = `${window.location.pathname}${serializeFinderQuery(query)}${window.location.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (next !== current) history.replaceState(null, "", next);
  }, [query]);

  useEffect(() => {
    const onPopState = () => {
      const next = readQuery();
      setQuery(next);
      setZipInput(next.zip ?? "");
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!query.zip) return;
    let cancelled = false;
    setStatus({ kind: "loading" });
    loadPlansForZip(query.zip)
      .then((result) => {
        if (cancelled) return;
        setStatus(
          result === null
            ? { kind: "not-served" }
            : { kind: "ready", plans: result.plans, tdus: result.tdus, generatedAt: result.generatedAt },
        );
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setStatus({ kind: "error", message: error instanceof Error ? error.message : String(error) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [query.zip]);

  const priced = useMemo<Priced[]>(() => {
    if (status.kind !== "ready" || query.usage <= 0) return [];
    return status.plans
      .map((plan) => ({ plan, bill: computeBill(plan.rate, plan.tdu_charges, query.usage) }))
      .filter(
        (row) =>
          matchesType(row.plan.rate_type, query.rateType) &&
          matchesTerm(row.plan.term_months, query.term) &&
          (!query.green100 || row.plan.renewable_pct === 100) &&
          (!query.hidePrepaid || !row.plan.prepaid) &&
          (!query.hideTou || !row.plan.time_of_use) &&
          (!query.hideNew || !row.plan.new_customer_only) &&
          (!query.verifiedOnly || row.plan.confidence === "high") &&
          matchesName(row.plan, query.q),
      )
      .sort((a, b) => comparePriced(a, b, query.sort));
  }, [status, query]);

  const activeFilters = filtersActive(query);

  function patch(partial: Partial<FinderQuery>) {
    setQuery((current) => ({ ...current, ...partial }));
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    const cleaned = zipInput.trim();
    if (/^\d{5}$/.test(cleaned)) patch({ zip: cleaned });
  }

  function resetFilters() {
    setQuery((current) => clearFilters(current));
  }

  return (
    <div className="finder">
      <form className="controls" onSubmit={submit}>
        <div className="field">
          <label htmlFor="zip">ZIP code</label>
          <input
            id="zip"
            inputMode="numeric"
            pattern="\d{5}"
            maxLength={5}
            placeholder="75001"
            value={zipInput}
            onChange={(e) => setZipInput(e.target.value.replace(/\D/g, ""))}
          />
        </div>

        <div className="field grow">
          <label htmlFor="usage">
            Monthly usage <strong>{query.usage.toLocaleString()} kWh</strong>
          </label>
          <input
            id="usage"
            type="range"
            min={100}
            max={4000}
            step={50}
            value={Math.min(4000, Math.max(100, query.usage))}
            onChange={(e) => patch({ usage: Number(e.target.value) })}
          />
          <div className="presets">
            {USAGE_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={query.usage === preset ? "preset active" : "preset"}
                onClick={() => patch({ usage: preset })}
              >
                {preset.toLocaleString()}
              </button>
            ))}
          </div>
        </div>

        <button className="primary" type="submit" disabled={!/^\d{5}$/.test(zipInput)}>
          Find plans
        </button>
      </form>

      {status.kind === "idle" && (
        <p className="hint">
          Enter your ZIP code and the number of kilowatt-hours you use in a typical month. Every plan
          is repriced at that exact usage rather than at the advertised 1,000 kWh.
        </p>
      )}
      {status.kind === "loading" && <p className="hint">Loading plans...</p>}
      {status.kind === "error" && <p className="error">Could not load plans: {status.message}</p>}
      {status.kind === "not-served" && (
        <p className="hint">
          {query.zip} is not in a deregulated part of Texas, so there are no retail plans to compare.
          Cities like Austin and San Antonio are served by municipal utilities.
        </p>
      )}

      {status.kind === "ready" && (
        <>
          <div className="summary">
            <span>
              <strong>{priced.length}</strong> plans in {query.zip} ({status.tdus.join(" + ")})
            </span>
          </div>
          <div className="filters">
            <div className="field">
              <label htmlFor="sort">Sort</label>
              <select
                id="sort"
                value={query.sort}
                onChange={(e) => patch({ sort: e.target.value as SortKey })}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="rate-type">Rate type</label>
              <select
                id="rate-type"
                value={query.rateType}
                onChange={(e) => patch({ rateType: e.target.value as RateTypeFilter })}
              >
                <option value="any">Any</option>
                <option value="fixed">Fixed</option>
                <option value="variable">Variable</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="term">Term</label>
              <select
                id="term"
                value={query.term}
                onChange={(e) => patch({ term: e.target.value as TermFilter })}
              >
                <option value="any">Any</option>
                <option value="m2m">Month-to-month</option>
                <option value="short">2–11 months</option>
                <option value="12">12 months</option>
                <option value="24">24 months</option>
                <option value="36">36 months or longer</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="renewable">Renewable</label>
              <select
                id="renewable"
                value={query.green100 ? "100" : "any"}
                onChange={(e) => patch({ green100: e.target.value === "100" })}
              >
                <option value="any">Any</option>
                <option value="100">100% only</option>
              </select>
            </div>
            <div className="field grow">
              <label htmlFor="q">Provider or plan</label>
              <input
                id="q"
                type="search"
                placeholder="Name"
                value={query.q}
                onChange={(e) => patch({ q: e.target.value })}
              />
            </div>
            <div className="filter-toggles">
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={query.hidePrepaid}
                  onChange={(e) => patch({ hidePrepaid: e.target.checked })}
                />
                Hide prepaid
              </label>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={query.hideTou}
                  onChange={(e) => patch({ hideTou: e.target.checked })}
                />
                Hide time of use
              </label>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={query.hideNew}
                  onChange={(e) => patch({ hideNew: e.target.checked })}
                />
                Hide new-customer only
              </label>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={query.verifiedOnly}
                  onChange={(e) => patch({ verifiedOnly: e.target.checked })}
                />
                Verified only
              </label>
              {activeFilters && priced.length > 0 && (
                <button type="button" className="text-button" onClick={resetFilters}>
                  Clear filters
                </button>
              )}
            </div>
          </div>
          {priced.length === 0 ? (
            <p className="hint">
              {activeFilters ? "No plans match these filters." : `No plans are available in ${query.zip}.`}
              {activeFilters && (
                <>
                  {" "}
                  <button type="button" className="text-button" onClick={resetFilters}>
                    Clear filters
                  </button>
                </>
              )}
            </p>
          ) : (
            <ol className="plans">
              {priced.map((row, rank) => (
                <PlanCard key={row.plan.plan_id} row={row} rank={rank + 1} usage={query.usage} />
              ))}
            </ol>
          )}
        </>
      )}
    </div>
  );
}
